import { Link } from "react-router-dom";
import { useMemo } from "react";
import { Flame, BookOpen, GraduationCap, Sparkles, ArrowRight, Check } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";
import { openBooking } from "@/lib/booking";

const A = "https://customer-assets-7cd3h4nn.emergentagent.net/job_pooja-marketplace-3/artifacts";
const IMG = {
  hero: `${A}/bda57ab2672a6f60_Vedic%20Learning%20at%20Sringeri%20Sharada%20Peetham.png`,
  collage: `${A}/42b8cd9435747fd7_Sringeri%20Veda%20Pathashala_%20Preserving%20Wisdom.png`,
  udupi: `${A}/8be7d2d039e63d75_Udupi%20Sri%20Krishna%20Matha_%20Vedic%20Learning.png`,
  dharmasthala: `${A}/4292042cf6d5e651_Siddhavana%20Gurukula_%20Tradition%20and%20Learning.png`,
  acharya: `${A}/6b0b0bd2aaa6dce0_Vedic%20Lessons%20in%20a%20Sunlit%20Temple%20Courtyard.png`,
};

const CEREMONIES = [
  "Griha Pravesha and Vastu Shanti",
  "Ganapati Homa and other traditional homas",
  "Vivaha and associated samskaras",
  "Upanayana and sacred-thread ceremonies",
  "Satyanarayana Puja",
  "Shraddha and other family religious observances",
  "Festival pujas and special devotional rituals",
];

const NETWORK = [];
const NETWORK_TEXT = [];
void NETWORK; void NETWORK_TEXT;

const COMMIT = [
  { t: "Increasing awareness", d: "Helping people discover the heritage of Vedic learning and the traditions associated with it." },
  { t: "Building connections", d: "Creating opportunities for meaningful engagement between devotees and the traditional priestly community." },
  { t: "Supporting visibility", d: "Providing a platform through which verified priest profiles and their areas of expertise can be discovered." },
  { t: "Respecting tradition", d: "Recognising that different Vedic lineages, Maths and regional traditions follow their own practices and requirements." },
  { t: "Enabling responsible technology", d: "Using digital tools to simplify discovery and coordination without attempting to replace the Acharya, the Guru–Shishya relationship or traditional religious authority." },
];

const H2 = ({ children }) => <h2 className="font-eczar text-3xl font-bold text-[#8B1A2B] md:text-4xl">{children}</h2>;

export default function SringeriPage() {
  useSeo({
    title: "Sringeri Sharada Peetham — Preserving the Eternal Wisdom of the Vedas | PurohithConnect Heritage",
    description: "A heritage initiative honouring Sri Sharada Peetham, Sringeri, the Guru–Shishya Parampara and Veda Pathashalas across Karnataka — Udupi, Dharmasthala, Poornaprajna Vidyapeetha. PurohithConnect connects devotees with tradition.",
    canonicalPath: "/patashala/sringeri",
    jsonLd: useMemo(() => ({
      "@context": "https://schema.org", "@type": "Article",
      headline: "Sringeri Sharada Peetham: Preserving the Eternal Wisdom of the Vedas",
      about: "Sri Sharada Peetham Sringeri, Veda Pathashala, Guru–Shishya Parampara, Advaita Vedanta",
      publisher: { "@type": "Organization", name: "PurohithConnect" },
    }), []),
  });

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader openWaitlist={() => openBooking({ location: "sringeri-header" })} />

      <main>
        {/* hero */}
        <section className="relative overflow-hidden" data-testid="sringeri-hero">
          <div className="absolute inset-0">
            <img src={IMG.hero} alt="Vedic students reciting under their Acharya at Sri Sharada Peetham, Sringeri" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A0E0A]/95 via-[#2A0E0A]/70 to-[#2A0E0A]/35" />
          </div>
          <div className="relative mx-auto flex min-h-[72vh] max-w-7xl flex-col justify-end px-4 py-16 md:px-8 md:py-24">
            <p className="font-baloo text-lg text-[#F0C98A]">ಪುರೋಹಿತ್ ಕನೆಕ್ಟ್ · ಪರಂಪರೆಯ ಗೌರವ</p>
            <h1 className="mt-2 max-w-4xl font-eczar text-4xl font-bold leading-tight text-[#FFFDF8] sm:text-5xl lg:text-6xl" data-testid="sringeri-title">
              Sringeri Sharada Peetham: Preserving the Eternal Wisdom of the Vedas
            </h1>
            <p className="mt-4 max-w-3xl font-mukta text-base text-[#F3E3C9] md:text-lg">
              Honouring the Guru–Shishya Parampara. Supporting Vedic learning. Connecting generations through Sanatana Dharma.
            </p>
          </div>
        </section>

        {/* intro */}
        <section className="mx-auto max-w-3xl px-4 py-16 md:px-8">
          <div className="space-y-5 font-mukta text-base leading-relaxed text-[#5C4438] md:text-lg">
            <p>Nestled on the banks of the sacred Tunga River in Karnataka, Sri Sharada Peetham, Sringeri, holds a revered place in India's spiritual and philosophical heritage. Associated with Jagadguru Adi Shankaracharya, Sringeri has been a centre of Advaita Vedanta, Sanskrit scholarship and the preservation of traditional knowledge for generations.</p>
            <p>The Veda Pathashala tradition represents an important part of this living heritage, where students learn under the guidance of their Acharyas, preserving the discipline, recitation and knowledge transmitted through the Guru–Shishya Parampara.</p>
            <p>At PurohithConnect, we recognise that the future of our traditions depends on the people who dedicate themselves to learning, practising and passing on this sacred knowledge.</p>
            <p>Our vision is to help build meaningful connections between devotees, trained purohits and the wider traditional priestly community—while respecting the institutions and lineages that have safeguarded these traditions for centuries.</p>
          </div>
        </section>

        {/* living heritage + collage */}
        <section className="bg-[#F3EADA]/60 py-16" data-testid="sringeri-heritage">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <div>
                <p className="font-baloo text-base text-[#B23A2B]">ಜೀವಂತ ಪರಂಪರೆ</p>
                <H2>The Sringeri Tradition: A Living Heritage</H2>
                <div className="mt-4 space-y-4 font-mukta text-base leading-relaxed text-[#5C4438]">
                  <p>The spiritual legacy of Sringeri extends beyond its sacred temples. It represents a continuing commitment to learning, spiritual discipline, philosophical inquiry and the preservation of India's ancient knowledge systems.</p>
                  <p>Vedic education is not merely the memorisation of sacred texts. It involves disciplined study, accurate recitation, guidance from experienced teachers and an understanding of the traditions within which the knowledge is practised.</p>
                  <p>Through their dedication, Acharyas and Vedic students help sustain the knowledge that continues to inform Hindu worship, religious observances and traditional samskaras. PurohithConnect seeks to bring greater awareness to this heritage and to the people who carry it forward.</p>
                </div>
              </div>
              <img src={IMG.collage} alt="Sri Sharada Peetham, Sringeri — Veda Pathashala life" loading="lazy" className="w-full rounded-[20px] shadow-lg" />
            </div>
          </div>
        </section>

        {/* vision + ceremonies */}
        <section className="mx-auto max-w-5xl px-4 py-16 md:px-8" data-testid="sringeri-vision">
          <p className="font-baloo text-base text-[#B23A2B]">ನಮ್ಮ ಸಂಕಲ್ಪ</p>
          <H2>Our Vision: Connecting Vedic Knowledge with Everyday Devotion</H2>
          <p className="mt-4 font-mukta text-base leading-relaxed text-[#5C4438] md:text-lg">
            Across India, families continue to seek knowledgeable and experienced purohits for important milestones and religious ceremonies, including:
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {CEREMONIES.map((c) => (
              <li key={c} className="flex items-start gap-3 rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] px-4 py-3 font-mukta text-sm text-[#3B221B] shadow-sm">
                <Check size={18} className="mt-0.5 shrink-0 text-[#3E6B4F]" /> {c}
              </li>
            ))}
          </ul>
          <div className="mt-6 space-y-4 font-mukta text-base leading-relaxed text-[#5C4438] md:text-lg">
            <p>Behind each ceremony lies a tradition of learning, preparation and practice. PurohithConnect aims to make it easier for devotees to discover suitable priests for their religious requirements, with clear service information and transparent arrangements.</p>
            <p className="font-semibold text-[#3B221B]">We believe that technology can complement traditional knowledge when it is designed with respect for the people, practices and customs it serves.</p>
          </div>
        </section>

        {/* commitment */}
        <section className="mx-auto max-w-5xl px-4 py-16 md:px-8" data-testid="sringeri-commitment">
          <div className="grid items-start gap-10 lg:grid-cols-[1fr_1.1fr]">
            <div>
              <p className="font-baloo text-base text-[#B23A2B]">ಮುಂದಿನ ಪೀಳಿಗೆಗೆ</p>
              <H2>Our Commitment to the Next Generation</H2>
              <p className="mt-4 font-mukta text-base leading-relaxed text-[#5C4438]">
                The future of Vedic traditions depends on continued learning, dedicated teachers and communities that value traditional knowledge. PurohithConnect aspires to contribute to this ecosystem by:
              </p>
              <img src={IMG.acharya} alt="An Acharya teaching Vedic students in a temple courtyard" loading="lazy" className="mt-6 w-full rounded-[18px] shadow-lg" />
            </div>
            <ul className="space-y-4">
              {COMMIT.map((c) => (
                <li key={c.t} className="flex gap-3 rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4 shadow-sm">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F3EADA] text-[#B23A2B]"><Sparkles size={16} /></span>
                  <div>
                    <h3 className="font-eczar text-base font-semibold text-[#3B221B]">{c.t}</h3>
                    <p className="mt-1 font-mukta text-sm leading-relaxed text-[#5C4438]">{c.d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* guiding belief */}
        <section className="relative overflow-hidden bg-[#8B1A2B] py-20 text-center" data-testid="sringeri-belief">
          <div className="mx-auto max-w-4xl px-4 md:px-8">
            <Flame size={30} className="mx-auto text-[#F0C98A]" />
            <p className="mt-4 font-baloo text-base text-[#F0C98A]">Our Guiding Belief</p>
            <p className="mt-3 font-eczar text-2xl font-bold leading-snug text-[#FFFDF8] md:text-4xl">
              Technology should not replace tradition. It should help people connect with it.
            </p>
            <p className="mx-auto mt-5 max-w-2xl font-mukta text-base leading-relaxed text-[#F3E3C9]">
              We envision a future where the knowledge preserved in traditional learning centres continues to reach families and communities, where experienced purohits can be discovered more easily, and where younger generations have greater opportunities to appreciate the richness of Sanatana Dharma.
            </p>
            <p className="mx-auto mt-5 max-w-2xl font-mukta text-base leading-relaxed text-[#F3E3C9]">
              With reverence for the Sringeri tradition and respect for all institutions dedicated to Vedic learning, PurohithConnect looks forward to contributing to the continued appreciation of India's spiritual and cultural heritage.
            </p>
            <p className="mt-6 font-eczar text-lg font-semibold text-[#F0C98A]">
              PurohithConnect — Connecting Devotees with Tradition. Preserving Heritage for Generations.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/patashala" data-testid="sringeri-to-patashala"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C99B55] px-7 py-3 font-mukta text-base font-semibold text-[#2A0E0A] transition-transform hover:-translate-y-0.5 hover:bg-[#d8ac67]">
                <GraduationCap size={18} /> For Patashala Students
              </Link>
              <button onClick={() => openBooking({ location: "sringeri-belief" })} data-testid="sringeri-book"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#F0C98A]/50 px-7 py-3 font-mukta text-base font-semibold text-[#F0C98A] transition-colors hover:bg-[#F0C98A]/10">
                <BookOpen size={18} /> Book a verified purohith <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </section>

        {/* disclaimer */}
        <section className="mx-auto max-w-3xl px-4 py-10 md:px-8" data-testid="sringeri-disclaimer">
          <p className="rounded-[14px] border border-[#A8722A]/25 bg-[#F7F1E7] px-5 py-4 text-center font-mukta text-xs leading-relaxed text-[#8A7B6F]">
            This page is an independent heritage initiative by PurohithConnect. It does not imply an official association, endorsement or integration with Sri Sharada Peetham, Sringeri, or any other institution unless formally confirmed.
          </p>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
